import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaFilter,
  FaChartPie,
  FaPlus,
  FaRoute,
  FaSpinner
} from 'react-icons/fa';
import SegmentDonut from '../charts/SegmentDonut';
import FunnelChart from '../charts/FunnelChart';
import './CustomerFunnelsTab.css';

/**
 * Tab 3: Conversion Funnels and Audience Segmentation.
 */
export default function CustomerFunnelsTab({
  report,
  funnel,
  comparison,
  checkpoints = null,
  actions = [],
  funnelActionIds = [],
  onFunnelActionsChange,
  onReviewCheckpoints = null,
  isCheckpointsLoading = false,
  onExportInteractions = null,
  isLoading = false,
  error = null
}) {
  const [selectedActionId, setSelectedActionId] = useState('');

  // Map action IDs to meta
  const actionsMap = actions.reduce((acc, act) => {
    acc[act.actionId] = act;
    return acc;
  }, {});

  const handleAddFunnelAction = () => {
    if (!selectedActionId || funnelActionIds.includes(selectedActionId) || funnelActionIds.length >= 16) return;
    const nextActions = [...funnelActionIds, selectedActionId];
    onFunnelActionsChange(nextActions);
    setSelectedActionId('');
  };

  const handleRemoveFunnelAction = (idToRemove) => {
    const nextActions = funnelActionIds.filter((id) => id !== idToRemove);
    onFunnelActionsChange(nextActions);
  };

  const segments = report?.segments || [];

  return (
    <div className="customer-funnels-tab" data-testid="customer-funnels-tab">
      {error && (
        <div className="funnels-error-banner" role="alert">
          {error}
        </div>
      )}

      {/* Audience Segmentation Donut Charts */}
      <div className="segments-section">
        <h4 className="section-title">
          <FaChartPie className="section-icon" /> Multi-Dimensional Audience Breakdown
        </h4>
        <div className="segments-grid">
          <SegmentDonut
            title="Device Breakdown"
            dimension="device"
            segments={segments}
          />
          <SegmentDonut
            title="Top Browsers"
            dimension="browser"
            segments={segments}
          />
          <SegmentDonut
            title="Geographic Distribution"
            dimension="country"
            segments={segments}
          />
        </div>
      </div>

      {/* 3. Conversion Funnel Builder */}
      <div className="funnel-builder-section">
        <div className="funnel-builder-header">
          <div>
            <h4 className="section-title">
              <FaFilter className="section-icon" /> Conversion Funnel Analysis
            </h4>
            <p className="section-desc">
              Define a sequence of registered journey actions to track conversion rates and user step drop-offs.
            </p>
          </div>

          <div className="funnel-action-picker">
            <select
              value={selectedActionId}
              onChange={(e) => setSelectedActionId(e.target.value)}
              className="action-select"
            >
              <option value="">-- Choose next step action --</option>
              {actions.map((act) => (
                <option
                  key={act.actionId}
                  value={act.actionId}
                  disabled={funnelActionIds.includes(act.actionId)}
                >
                  {act.name} ({act.captureKey})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-add-step"
              onClick={handleAddFunnelAction}
              disabled={!selectedActionId || funnelActionIds.length >= 16}
              title={funnelActionIds.length >= 16 ? 'Maximum 16 steps allowed in funnel' : 'Add Step'}
            >
              <FaPlus /> Add Step
            </button>
            <span className="funnel-counter-badge" title="Backend supports between 2 and 16 sequence steps">
              {funnelActionIds.length} / 16 steps
            </span>
          </div>
        </div>

        {/* 1 Step callout: minimum 2 steps required */}
        {funnelActionIds.length === 1 && (
          <div className="funnel-min-step-callout" data-testid="funnel-min-step-callout">
            ℹ️ Add at least 1 more step to calculate conversion funnel metrics (minimum 2 steps, maximum 16 steps).
          </div>
        )}

        {/* Selected Steps Chips */}
        {funnelActionIds.length > 0 && (
          <div className="funnel-sequence-chips" data-testid="funnel-sequence-chips">
            <span className="chips-label">Funnel Path:</span>
            {funnelActionIds.map((actId, idx) => {
              const meta = actionsMap[actId];
              return (
                <div key={actId} className="funnel-chip">
                  <span className="chip-step-num">{idx + 1}</span>
                  <span className="chip-name">{meta?.name || actId.substring(0, 8)}</span>
                  <button
                    type="button"
                    className="chip-remove-btn"
                    onClick={() => handleRemoveFunnelAction(actId)}
                    title="Remove step"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Funnel Chart Visualizer */}
        <FunnelChart
          funnelData={funnel}
          actionsMap={actionsMap}
          isLoading={isLoading}
        />
      </div>

      {/* 4. Checkpoints Review Section */}
      {funnelActionIds.length >= 2 && funnelActionIds.length <= 16 && onReviewCheckpoints && (
        <div className="checkpoints-section" data-testid="checkpoints-review-section">
          <div className="checkpoints-header">
            <div>
              <h4 className="section-title">
                <FaRoute className="section-icon" /> Checkpoint & Path Deviation Review
              </h4>
              <p className="section-desc">
                Evaluate whether users follow this funnel directly or experience detours, loops, and skipped steps.
              </p>
            </div>
            <button
              type="button"
              className="btn-review-checkpoints"
              onClick={() => onReviewCheckpoints(funnelActionIds)}
              disabled={isCheckpointsLoading}
              data-testid="btn-review-checkpoints"
            >
              {isCheckpointsLoading ? <FaSpinner className="spinning" /> : <FaRoute />}
              <span>{isCheckpointsLoading ? 'Analyzing...' : 'Analyze Path Deviations'}</span>
            </button>
          </div>

          {checkpoints && (
            <div className="checkpoints-stats-grid" data-testid="checkpoints-stats-grid">
              <div className="checkpoint-stat-card">
                <span className="stat-label">On Path</span>
                <span className="stat-value">{checkpoints.onPath ?? 0}</span>
              </div>
              <div className="checkpoint-stat-card">
                <span className="stat-label">Deviations</span>
                <span className="stat-value">{checkpoints.completedWithDeviations ?? 0}</span>
              </div>
              <div className="checkpoint-stat-card">
                <span className="stat-label">Skips</span>
                <span className="stat-value">{checkpoints.skips ?? 0}</span>
              </div>
              <div className="checkpoint-stat-card">
                <span className="stat-label">Loops</span>
                <span className="stat-value">{checkpoints.loops ?? 0}</span>
              </div>
              <div className="checkpoint-stat-card">
                <span className="stat-label">Detours</span>
                <span className="stat-value">{checkpoints.detours ?? 0}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CustomerFunnelsTab.propTypes = {
  report: PropTypes.object,
  funnel: PropTypes.object,
  comparison: PropTypes.object,
  checkpoints: PropTypes.object,
  actions: PropTypes.array,
  funnelActionIds: PropTypes.array,
  onFunnelActionsChange: PropTypes.func.isRequired,
  onReviewCheckpoints: PropTypes.func,
  isCheckpointsLoading: PropTypes.bool,
  onExportInteractions: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.string
};
